import { Link } from "react-router-dom";
import {
  ArrowRight,
  Users,
  Building2,
  GraduationCap,
  ShieldCheck,
  Brain,
  SearchCheck,
  CheckCircle2,
  LockKeyhole,
} from "lucide-react";

import SEO from "@/components/site/SEO";
import CTASection from "@/components/site/CTASection";
import FAQSection from "@/components/site/FAQSection";

import {
  IMAGES,
  COMPANY,
  FAQS_HOME,
} from "@/data/content";

const PRIMARY_ROUTES = [
  {
    title: "I want to become a train driver",
    body: "Application, CV, interview, psychometric and professional readiness support.",
    icon: Users,
    to: "/for-individuals",
    cta: "Start here",
  },
  {
    title: "I represent a railway organisation",
    body: "Training, development, competence and assurance support for safety-critical teams.",
    icon: Building2,
    to: "/for-organisations",
    cta: "Organisation support",
  },
  {
    title: "I need a competence management system",
    body: "TRAC-CMS supports competence records, assessments, evidence and compliance visibility.",
    logo: "/TRAC-CMS.svg",
    to: "/trac-cms",
    cta: "Explore TRAC-CMS",
  },
  {
    title: "I need a learning management system",
    body: "TRAC-Learn supports courses, CPD, knowledge checks, learner progress and training evidence.",
    logo: "/TRAC-Learn.svg",
    to: "/trac-learn",
    cta: "Explore TRAC-Learn",
  },
];

const EXAMPLE_COURSES = [
  {
    title: "VR Traction course",
    body: "Explore immersive traction training for cab systems, fault scenarios and operational familiarity.",
    to: "/vr-traction",
  },
  {
    title: "VR PTS course",
    body: "Discover a safe, scenario-based VR course for trackside hazard awareness and Personal Track Safety readiness.",
    to: "/vr-pts",
  },
  {
    title: "eLearning course",
    body: "Review a structured online course with modules, assessments, CPD and evidence capture.",
    to: "/elearning",
  },
];

const ECOSYSTEM = [
  {
    title: "Discover",
    body: "Railway Careers UK helps people understand railway roles, employers and routes into the industry.",
    icon: SearchCheck,
  },
  {
    title: "Prepare",
    body: "Train Driver Foundation and Train Driver Psychometrics support applicants preparing for selection.",
    icon: Brain,
  },
  {
    title: "Develop",
    body: "UKTDA supports professional development, operational thinking and railway capability.",
    icon: GraduationCap,
  },
  {
    title: "Assure",
    body: "TRAC connects training, competence, evidence, risk and assurance oversight.",
    icon: ShieldCheck,
  },
];

const TRAC_POINTS = [
  "Competence management",
  "Learning and CPD",
  "Assessment evidence",
  "Compliance visibility",
  "Risk and assurance reporting",
  "ISO 27001-aligned security thinking",
];

export default function Home() {
  return (
    <>
      <SEO
        title="UK Train Driver Academy | Training, Recruitment, Competence and Assurance"
        description="UKTDA supports train driver and safety critical recruitment, rail training, competence management, compliance and operational assurance."
        path="/"
      />

      {/* HERO */}
      <section
        className="relative overflow-hidden bg-navy-deep text-white"
        data-testid="home-hero"
      >
        <div className="absolute inset-0">
          <img
            src={IMAGES.heroCab}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-navy-deep via-navy-deep/95 to-navy/80" />
          <div className="absolute inset-0 grid-bg opacity-[0.08]" />
        </div>

        <div className="container-rail relative py-20 md:py-28">
          <div className="max-w-6xl">
            <div className="flex flex-col sm:flex-row sm:items-center gap-6 md:gap-8">
              <div className="h-28 w-28 md:h-40 md:w-40 shrink-0 rounded-2xl bg-white shadow-xl ring-1 ring-white/30 p-3 flex items-center justify-center">
                <img
                  src="/UKTDA_transparent.png"
                  alt="UKTDA logo"
                  className="max-h-full max-w-full object-contain"
                  loading="eager"
                />
              </div>

              <div>
                <p className="label-eyebrow text-safety mb-2">
                  UK Train Driver Academy Ltd
                </p>

                <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight text-white leading-none">
                  UKTDA
                </h1>

                <p className="mt-4 text-lg md:text-2xl font-semibold text-slate-100 leading-snug max-w-3xl">
                  Training, Risk and Assurance Compliance
                </p>

                <p className="mt-3 text-sm md:text-base text-slate-300 leading-relaxed max-w-3xl">
                  Professional rail training, competence support, safety-critical recruitment tools and assurance-led learning solutions.
                </p>
              </div>
            </div>

            <div className="mt-10 max-w-5xl">
              <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-7xl text-white leading-[1.02] text-balance">
                Railway training, recruitment preparation and competence assurance.
              </h2>

              <p className="mt-7 max-w-3xl text-base md:text-lg text-slate-300 leading-relaxed">
                UKTDA supports aspiring train drivers, railway professionals and organisations
                through specialist preparation, training, learning systems and competence
                management products.
              </p>

              <div className="mt-10 flex flex-wrap gap-3">
                <Link
                  to="/for-individuals"
                  data-testid="hero-cta-individuals"
                  className="inline-flex items-center gap-2 bg-safety hover:bg-[#e66e00] text-white font-semibold px-5 py-3 rounded-md transition-colors"
                >
                  I want to become a train driver <ArrowRight className="h-4 w-4" />
                </Link>

                <Link
                  to="/trac-cms"
                  data-testid="hero-cta-trac"
                  className="inline-flex items-center gap-2 border border-white/30 bg-white/5 hover:bg-white/10 text-white font-semibold px-5 py-3 rounded-md transition-colors"
                >
                  Explore TRAC
                </Link>

                <Link
                  to="/for-organisations"
                  data-testid="hero-cta-organisations"
                  className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/5 text-white font-semibold px-5 py-3 rounded-md transition-colors"
                >
                  Organisation support
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRIMARY ROUTES */}
      <section
        className="py-16 md:py-20 bg-white"
        data-testid="home-primary-routes"
      >
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">Choose your route</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              What are you looking for?
            </h2>

            <p className="mt-4 text-slate-600 leading-relaxed">
              UKTDA has several brands and products. Start with the option that best matches
              what you need.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {PRIMARY_ROUTES.map((route) => {
              const Icon = route.icon;

              return (
                <Link
                  key={route.title}
                  to={route.to}
                  className="group rounded-md border border-slate-200 bg-slate-50 p-6 hover:bg-white hover:border-safety/60 transition-colors"
                >
                  <div className="inline-flex h-16 w-16 items-center justify-center rounded-xl bg-navy text-white group-hover:bg-safety transition-colors p-2">
                    {route.logo ? (
                      <img
                        src={route.logo}
                        alt={`${route.title} logo`}
                        className="max-h-full max-w-full object-contain"
                        loading="lazy"
                      />
                    ) : (
                      <Icon className="h-7 w-7" />
                    )}
                  </div>

                  <h3 className="mt-5 font-display font-bold text-lg text-navy">
                    {route.title}
                  </h3>

                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {route.body}
                  </p>

                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-navy group-hover:text-safety">
                    {route.cta} <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* EXAMPLE COURSES */}
      <section
        className="py-16 md:py-20 bg-slate-50"
        data-testid="home-example-courses"
      >
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">Explore example training</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Example course formats for railway organisations.
            </h2>

            <p className="mt-4 text-slate-600 leading-relaxed">
              See how immersive VR and structured eLearning can support safer, more engaging railway training.
            </p>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {EXAMPLE_COURSES.map((course) => (
              <Link
                key={course.title}
                to={course.to}
                className="group rounded-md border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:border-safety/60"
              >
                <p className="text-xs font-bold uppercase tracking-wide text-safety">
                  Example
                </p>

                <h3 className="mt-4 font-display font-bold text-xl text-navy">
                  {course.title}
                </h3>

                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                  {course.body}
                </p>

                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-safety">
                  View example <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT UKTDA DOES */}
      <section
        className="py-16 md:py-20 bg-slate-50 border-y border-slate-200"
        data-testid="home-what-uktda-does"
      >
        <div className="container-rail grid lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-16 items-start">
          <div>
            <p className="label-eyebrow text-safety">What UKTDA does</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl lg:text-5xl text-navy leading-tight">
              We support the full railway capability journey.
            </h2>
          </div>

          <div className="space-y-5 text-base leading-relaxed text-slate-600">
            <p>
              {COMPANY.legalName} is the parent company behind Train Driver Foundation,
              Train Driver Psychometrics, Railway Careers UK and TRAC.
            </p>

            <p>
              We help individuals prepare properly for railway careers and help organisations
              manage training, competence, compliance and assurance more effectively.
            </p>

            <p>
              Everything is built around safety-critical thinking: clear evidence, professional
              standards, operational judgement, risk awareness and structured development.
            </p>
          </div>
        </div>

        <div className="container-rail mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {ECOSYSTEM.map((item) => {
            const Icon = item.icon;

            return (
              <div
                key={item.title}
                className="rounded-md border border-slate-200 bg-white p-6"
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-navy text-white">
                  <Icon className="h-5 w-5" />
                </div>

                <h3 className="mt-4 font-display font-bold text-lg text-navy">
                  {item.title}
                </h3>

                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  {item.body}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* TRAC */}
      <section
        className="py-20 md:py-24 bg-slate-950 text-white"
        data-testid="home-trac"
      >
        <div className="container-rail grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-16 items-start">
          <div>
            <p className="label-eyebrow text-safety">TRAC platform</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl lg:text-5xl text-white leading-tight">
              Training, Risk and Assurance Compliance.
            </h2>

            <p className="mt-6 text-slate-300 leading-relaxed">
              TRAC is being developed as a joined-up platform for railway organisations
              that need clearer oversight of competence, learning, evidence, risk and
              compliance.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/trac-cms"
                data-testid="home-trac-cms"
                className="inline-flex items-center gap-2 bg-safety hover:bg-[#e66e00] text-white font-semibold px-5 py-3 rounded-md transition-colors"
              >
                TRAC-CMS <ArrowRight className="h-4 w-4" />
              </Link>

              <Link
                to="/trac-learn"
                data-testid="home-trac-learn"
                className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/5 text-white font-semibold px-5 py-3 rounded-md transition-colors"
              >
                TRAC-Learn <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="rounded-md border border-white/10 bg-white/[0.04] p-6 md:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <div className="h-28 w-28 md:h-32 md:w-32 shrink-0 rounded-2xl bg-white flex items-center justify-center p-3 shadow-lg">
                <img
                  src="/TRAC.svg"
                  alt="TRAC logo"
                  className="max-h-full max-w-full object-contain"
                  loading="lazy"
                />
              </div>

              <div>
                <p className="font-mono-ibm text-safety text-[12px] tracking-widest">
                  TRAC
                </p>

                <h3 className="font-display font-bold text-2xl text-white">
                  One platform direction. Two core products.
                </h3>

                <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                  TRAC-CMS for competence management. TRAC-Learn for learning,
                  CPD and training evidence.
                </p>
              </div>
            </div>

            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {TRAC_POINTS.map((point) => (
                <div
                  key={point}
                  className="flex items-start gap-3 rounded-md border border-white/10 bg-white/[0.04] p-4"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-safety" />
                  <p className="text-sm text-slate-300">{point}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-md border border-safety/30 bg-safety/10 p-5">
              <div className="flex items-start gap-3">
                <LockKeyhole className="mt-0.5 h-5 w-5 shrink-0 text-safety" />

                <p className="text-sm leading-relaxed text-slate-200">
                  TRAC is being positioned with enterprise SaaS expectations, including
                  access control, audit evidence, policy governance and ISO 27001-aligned
                  information security thinking.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL STRIP */}
      <section
        className="py-14 bg-safety text-white"
        data-testid="home-assurance-strip"
      >
        <div className="container-rail grid gap-6 md:grid-cols-3">
          {[
            "Clear routes for individuals and organisations.",
            "Preparation aligned to safety-critical railway expectations.",
            "TRAC connects training, competence, evidence and assurance.",
          ].map((item) => (
            <div key={item} className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-white" />
              <p className="font-semibold leading-relaxed">{item}</p>
            </div>
          ))}
        </div>
      </section>

      <FAQSection
        items={FAQS_HOME}
        title="About UKTDA"
        intro="Common questions about UKTDA, our brands, TRAC and what we do."
        schemaId="faq-home"
      />

      <CTASection
        eyebrow="Speak to our team"
        title="Discuss training, recruitment preparation, competence management or organisational assurance."
        intro="Whether you are preparing for a train driver career or managing safety-critical training and competence, UKTDA can help you take the next structured step."
        primary={{
          label: "Contact us",
          to: "/contact",
          testId: "home-cta-contact",
        }}
        secondary={{
          label: "Explore TRAC",
          to: "/trac",
          testId: "home-cta-trac",
        }}
      />
    </>
  );
}